import React, { useEffect, useState } from "react";

//** MUI Components */
import { Box, Fade, Skeleton } from "@mui/material";
import { useOutletContext } from "react-router-dom";

//** NDBX */
import {
  Button,
  ButtonStack,
  Section,
  NotificationCard,
} from "@arp/react-ndbx";

//** Context  */
import useNewBusiness from "../../hooks/useNewBusiness.ts";
import usePreQualification from "../../hooks/usePreQualification.ts";
import usePreQualificationPages from "../../hooks/usePreQualificationPages.ts";
import useSharedData from "../../hooks/useSharedData.ts";

//** ARP Components */
import Page from "../../components/Page";

//** Hooks */
import { useGetInterviewOutcome } from "../../hooks/dataHooks/useGetInterviewOutcome.js";

//** Config  */
import {
  AURA_OUTCOME,
  Application,
  TRACKIFY_EVENT,
  sendEmailModalContent,
} from "../../config/const/enums.js";
import { pageOrder } from "../../config/pageInfo/pageOrder";
import { policyOutcomeDisclaimers } from "../../config/disclaimers/disclaimers.jsx";

//** Utilities */
import FadeInOut from "../../utilities/fadeInOut.jsx";
import { isObjEmpty } from "../../utilities/utils.js";

//** Page components  */
import OutcomeTermsAndConditions from "../../components/pageLevel/policyOutcome/OutcomeTermsAndConditions.jsx";
import SuccessfulHeadlineHeader from "../../components/pageLevel/policyOutcome/SuccessfulHeadlineHeader.jsx";
import SuccessfulOutcomeDetail from "../../components/pageLevel/policyOutcome/SuccessfulOutcomeDetail.jsx";
import SendEmailModal from "../../components/pageLevel/sendEmailModal/SendEmailModal.jsx";

import { isEmpty } from "lodash";
import { trackifyPageChange } from "@allianz/tracking";

// ----------------------------------------------------------------

const SuccessfulOutcome = (props) => {
  const {
    fadeIn,
    handleOnChange,
    currentPage,
    frequency,
    dataSet,
    isDirty,
    isValid,
    retrieveQuoteJourney,
    isOnline = true,
    isRetrievePolicyExpired = false,
    errors,
    setValue,
    handleSubmit,
    handleInputChange,
    policyOutcomeAcceptTerms,
  } = {
    ...props,
  };

  /* #region Send Email */
  const [openSendEmailModal, setOpenSendEmailModal] = useState(false);
  const handleSendEmailOpen = () => {
    trackifyPageChange(TRACKIFY_EVENT.SEND_EMAIL_POLICY_OUTCOME);
    setOpenSendEmailModal(true);
  };
  const handleSendEmailClose = (event, reason) => {
    if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
      setOpenSendEmailModal(false);
      setSendQuoteEmailSuccess(false);
    }
  };
  const [sendQuoteEmailSuccess, setSendQuoteEmailSuccess] = useState(false);
  const handleSuccessEmailModal = () => {
    setSendQuoteEmailSuccess(true);
  };
  /* #endregion */

  //* If there are any exclusions across any of the cover, we need to
  //* show the "special" T&Cs
  //* However, if we remove a cover, with exclusions and there
  //* is no other cover with exclusions, we have to show the
  //* default T&Cs.
  // const [showExclusionsTerms, setShowExclusionsTerms] = useState(true);

  //* New Business data
  const { data: newBusinessState } = useNewBusiness();
  let showExclusionsTerms = newBusinessState?.covers?.anyExclusionsOrLoadings;
  //   let showExclusionsTerms = true;

  const handleCoverCheckboxCheck = (boolVal) => {
    // const showSpecialTerms = boolVal;
    // setShowExclusionsTerms(showSpecialTerms);
    //** Update the acceptTerms checkbox */
    handleInputChange({
      name: "policyOutcomeAcceptTerms",
      value: policyOutcomeAcceptTerms,
    });
  };

  useEffect(() => {
    trackifyPageChange(currentPage?.identifier?.toLowerCase());
  }, []);

  return (
    <>
      <Fade in={fadeIn} timeout={700}>
        <Box>
          <SuccessfulHeadlineHeader onChange={(e) => handleOnChange(e)} />
        </Box>
      </Fade>
      <Page title={currentPage.title} identifier={currentPage.identifier}>
        {retrieveQuoteJourney && (
          <Section md={7} mb={4}>
            <FadeInOut show={1 === 1} duration={100} style={{ width: "100%" }}>
              <Box>
                <NotificationCard
                  cardType="info"
                  title="Application retrieval important information"
                  message={
                    policyOutcomeDisclaimers({ isOnline: true })
                      .policyOutcomeInfo[0].POLICY_OUTCOME_RETRIEVAL
                  }
                />
              </Box>
            </FadeInOut>
          </Section>
        )}
        <Fade in={fadeIn} timeout={500}>
          <Box>
            <SuccessfulOutcomeDetail
              person="personOne"
              frequency={frequency}
              application={Application.ONLINE}
              // dataSet={dataSet}
              onCheckedChange={(val) => handleCoverCheckboxCheck(val)}
              isRetrievePolicyExpired={isRetrievePolicyExpired}
              retrieveQuoteJourney={retrieveQuoteJourney}
              isUWUser={false}
            />
            {showExclusionsTerms === true ? (
              <OutcomeTermsAndConditions
                showSpecialTerms={showExclusionsTerms}
                onLine={true}
                dataSet={dataSet}
                setValue={setValue}
                handleSubmit={handleSubmit}
              />
            ) : null}
          </Box>
        </Fade>
        <Section mt={8}>
          <ButtonStack>
            <Button
              variant="contained"
              color="secondary"
              disabled={!isDirty || !isEmpty(errors) || isRetrievePolicyExpired}
              onClick={handleSendEmailOpen}
            >
              SAVE AND EMAIL
            </Button>
            <Button
              variant="contained"
              color="primary"
              type="submit"
              disabled={!isDirty || !isEmpty(errors)}
            >
              PURCHASE POLICY
            </Button>
          </ButtonStack>
        </Section>
        <SendEmailModal
          openSendEmailModal={openSendEmailModal}
          handleSendEmailClose={handleSendEmailClose}
          handleSuccessEmailModal={handleSuccessEmailModal}
          sendQuoteEmailSuccess={sendQuoteEmailSuccess}
          sendEmailImg={sendEmailModalContent.SEND_EMAIL_IMAGE}
        />
      </Page>
    </>
  );
};

//----------------------------------------------------------------

export default function PolicyOutcomeOnline() {
  const { state, funcs } = usePreQualificationPages();
  const { currentPage } = { ...state };
  const decline = () => {
    funcs.validateAndProceed(pageOrder.PP_POLICY_OUTCOME_DECLINE, true);
  };

  const {
    methods: [methods],
    showSecond: [showSecond],
    isDirty: [isDirty],
    isValid: [isValid],
    errors: [errors],
  } = useOutletContext();

  const { trigger, setValue, handleSubmit } = { ...methods };

  //* PreQual state data
  const { updateStateOnRetrieval, dataSet, handleInputChange } =
    usePreQualification();

  //* Shared data
  const {
    accessToken,
    retrieveQuoteJourney,
    isOutcomeDataLoaded,
    handleEventChange,
  } = useSharedData();
  const [policyOutcomeData, setPolicyOutcomeData] = useState({}); //* what and when data gets displayed.

  //* New Business data
  const {
    data: newBusinessState,
    setInitialData,
    isDataUpdating,
  } = useNewBusiness();

  //* Interview question outcome from Aura
  //* Note: isInitialLoading is derived from isLoading && isFetching
  const {
    isLoading,
    isFetching,
    isInitialLoading,
    data: interviewOutcomeData,
    error,
    refetch,
  } = useGetInterviewOutcome({
    accessToken:
      accessToken ??
      "eyJhbGciOiJIUzI1NiJ9.eyJhdWQiOlsiaHR0cHM6Ly9kZXYuZWxpZmVpbnN1cmUuY29tLmF1L29hdXRoMi90b2tlbiJdLCJpc3MiOiJ0ZXJtbGlmZS1hcGkuZGV2Iiwic3ViIjoidGVybWxpZmUtYXBpLmRldi5qd3QiLCJpYXQiOjE3NTQ1NDA1MjIsImV4cCI6MTc1NDU2OTMyMiwianRpIjoiMmFmM2M0NmUtYjA5Ny00NmU4LTkzODktMzFkYjM3NDU3Y2MyIiwicWlkIjoiNjM1UTcwMjE1NzlBTFAiLCJlbHRpZCI6ImUwZTBlYTYwLTliZDgtNDRmZC1hMzFjLWM2YTczMmM0MzIxNSIsIm9rdGFVc2VySWQiOiIwMHU2cHUzbzR1alZOUWdNdDNsNyIsImNjX3VzZXIiOmZhbHNlfQ.byB6oWgWLTc5OuIYFh0SUTsmfgJgzYwBNmexSFiIL0k",

    enableGet: !retrieveQuoteJourney, //* Don't call on a retrieved policy.
  });

  useEffect(() => {
    const updateLocalState = async () => {
      if (
        isInitialLoading ||
        interviewOutcomeData == null ||
        retrieveQuoteJourney === true
      )
        return;

      if (isOutcomeDataLoaded === false) {
        //* we only want to do this once when we get the Interview Outcome.
        await updateStateOnRetrieval({
          data: interviewOutcomeData,
          updateType: "policyInfo",
        });

        //* We now need to put the outcome data in to our local state
        setInitialData(interviewOutcomeData);

        handleEventChange({
          name: "isOutcomeDataLoaded",
          value: true,
        });
      }
      setPolicyOutcomeData(interviewOutcomeData); //* Used for display.

      trigger();
    };
    updateLocalState();
  }, [isInitialLoading]); //? If this is a new Policy. Coming directly from the Interview Questions

  useEffect(() => {
    const updateLocalState = async () => {
      if (
        isDataUpdating ||
        newBusinessState == null ||
        retrieveQuoteJourney === false
      )
        return;
      setPolicyOutcomeData(newBusinessState);
    };
    updateLocalState();
  }, [isDataUpdating]); //? If this is an existing Policy. Coming from a Policy retrieval

  const [frequency, setFrequency] = useState("monthly");
  const handleOnChange = (e) => {
    if (!e) return;
    setFrequency(e);
  };

  let showData = !isObjEmpty(policyOutcomeData);
  const isOnline = dataSet?.application === Application.ONLINE;

  //----------------------------------------------------------------

  return (
    <>
      <FadeInOut
        show={showData === false}
        duration={500}
        style={{ width: "100%" }}
      >
        <Skeleton
          sx={{ bgcolor: "#F1F9FA" }}
          animation="wave"
          variant="rounded"
          width={"100%"}
          height={"75vh"}
        />
      </FadeInOut>
      {showData === true &&
      policyOutcomeData?.auraStatusDisplay === AURA_OUTCOME.ACC.toString() ? (
        <>
          <SuccessfulOutcome
            fadeIn={showData}
            handleOnChange={handleOnChange}
            currentPage={currentPage}
            frequency={frequency}
            dataSet={dataSet}
            isDirty={isDirty}
            isValid={isValid}
            retrieveQuoteJourney={retrieveQuoteJourney}
            isOnline={isOnline}
            isRetrievePolicyExpired={false} //* isRetrievePolicyExpired is always false for Online
            errors={errors}
            setValue={setValue}
            handleSubmit={handleSubmit}
            handleInputChange={handleInputChange}
            policyOutcomeAcceptTerms={dataSet?.policyOutcomeAcceptTerms}
          />
        </>
      ) : null}

      {showData === true &&
      (policyOutcomeData?.auraStatusDisplay === AURA_OUTCOME.DCL.toString() ||
        policyOutcomeData?.auraStatusDisplay === AURA_OUTCOME.PP.toString() ||
        policyOutcomeData?.auraStatusDisplay === AURA_OUTCOME.RUW.toString())
        ? decline()
        : null}
    </>
  );
}
